import { Component, input, output, ChangeDetectionStrategy } from '@angular/core';
import {
  FormGroup,
  FormControl,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { ERROR_MESSAGE } from 'src/app/_helpers/utils';
import { FormBase } from 'src/app/_models/form-base';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import {
  MatFormField,
  MatLabel,
  MatSuffix,
  MatError,
} from '@angular/material/form-field';
import { MacAddressFormatDirective } from 'src/app/_helpers/mac-address-format.directive';

@Component({
    selector: 'app-underlined-form-field',
    templateUrl: './underlined-form-field.component.html',
    styleUrls: ['./underlined-form-field.component.scss'],
    imports: [
        MatFormField,
        FormsModule,
        ReactiveFormsModule,
        MatLabel,
        MatInput,
        MatIcon,
        MatSuffix,
        MatError,
        MacAddressFormatDirective,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class UnderlinedFormFieldComponent {
  /** Field form base (required) */
  field = input<FormBase<string>>();

  /** Parent FormGroup containing the control (required) */
  parentForm = input<FormGroup>();

  /** Name of the icon displayed within the input field (optional) */
  iconName = input<string | undefined>(undefined);

  /** Text to display in the tooltip (optional) */
  tooltipText = input<string | undefined>(undefined);

  /** Disables the input field (optional, default: 'false') */
  isDisabled = input<boolean>(false);

  /** Warning message displayed when validation fails (optional) */
  warningMessage = input<string | undefined>(undefined);

  /** Controls whether the warning message should be shown (optional, default: 'false') */
  showWarning = input<boolean>(false);

  /** Controls whether the mac address format directive should be used (optional, default: 'false') */
  useMacAddressFormat = input<boolean>(false);

  /** Emits an event when the minus button is clicked */
  minusButtonClick = output<void>();

  /** Emits an event when the plus button is clicked */
  plusButtonClick = output<void>();

  /**
   * Indicates whether the password field is in visible mode.
   */
  hidePassword = true;

  /**
   * Retrieves the `FormControl` associated with this form field.
   * @returns The `FormControl` instance from the parent form.
   */
  public get control(): FormControl | null {
    const form = this.parentForm();
    const f = this.field();
    if (!form || !f) return null;
    return form.controls[f.key] as FormControl;
  }

  /**
   * Determines whether the field has validation errors.
   * @returns `true` if the field is invalid and has been touched, otherwise `false`.
   */
  public get hasErrors(): boolean {
    const c = this.control;
    return !!c && c.invalid && c.touched;
  }

  /**
   * Retrieves a list of validation error messages for the field.
   * @returns An array of error messages corresponding to validation failures.
   */
  public get errorsList(): string[] {
    const c = this.control;
    if (!c || !c.errors) return [];
    return Object.keys(c.errors || {}).map((err) => {
      const errorFn = ERROR_MESSAGE[err as keyof typeof ERROR_MESSAGE];
      return errorFn ? errorFn(c.getError(err) as never) : 'Unknown error';
    });
  }

  /**
   * Determines the input type (e.g., `'password'`, `'text'`, etc.).
   * If the field type is `'password'`, it switches between `'text'` and `'password'` based on visibility.
   * @returns The appropriate input type string.
   */
  public get inputType(): string {
    const f = this.field();
    if (!f) return 'text';
    return f.type === 'password' && !this.hidePassword ? 'text' : f.type;
  }

  /**
   * Emits the `minusButtonClick` event (used for decrementing number fields).
   */
  minusButtonClicked(): void {
    this.minusButtonClick.emit?.();
  }

  /**
   * Emits the `plusButtonClick` event (used for incrementing number fields).
   */
  plusButtonClicked(): void {
    this.plusButtonClick.emit?.();
  }
}
